import React, { useEffect, useRef } from "react";
import { Button, Tag, Tooltip } from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useDispatchRoot, useSelectorRoot } from "@/store/hooks";
import {
  pipelineStatusActions,
  selectPipelineStatusByBaseFileId,
  selectReconvertByBaseFileId,
} from "@/features/file-management/stores/pipelineStatus/pipelineStatus.slice";
import { PipelineOverallStatus } from "@/api/conversion.api";

// Only these formats ever go through the Rvt2Scz conversion pipeline -- no point
// fetching/polling pipeline status for anything else.
const CONVERTIBLE_FORMATS = ["rvt"];

// Poll while the job is still in flight so "Converting" flips to "Completed"/"Failed"
// without a manual page refresh; stop once it reaches a terminal state.
const POLL_INTERVAL_MS = 5000;
const NON_TERMINAL_STATUSES = new Set([
  PipelineOverallStatus.Queued,
  PipelineOverallStatus.Preparing,
  PipelineOverallStatus.Running,
]);

const STATUS_TAG: Record<
  PipelineOverallStatus,
  { color: string; labelKey: string }
> = {
  [PipelineOverallStatus.Queued]: { color: "default", labelKey: "convert_status_queued" },
  [PipelineOverallStatus.Preparing]: { color: "gold", labelKey: "convert_status_preparing" },
  [PipelineOverallStatus.Running]: { color: "blue", labelKey: "convert_status_running" },
  [PipelineOverallStatus.Completed]: { color: "green", labelKey: "convert_status_completed" },
  [PipelineOverallStatus.PartialSuccess]: { color: "orange", labelKey: "convert_status_partial_success" },
  [PipelineOverallStatus.Failed]: { color: "red", labelKey: "convert_status_failed" },
};

// Statuses that mean the conversion ended without a usable result -- the case the inline retry
// button exists for. A Completed job can still be reconverted, but only from the context menu:
// nothing is wrong with it, so the cell does not advertise a retry.
const RETRYABLE_STATUSES = new Set([
  PipelineOverallStatus.Failed,
  PipelineOverallStatus.PartialSuccess,
]);

export interface ConversionStatusCellProps {
  baseFileId?: string;
  format?: string;
  /** Document version -- must match the StreamFile's BaseMajorRev for a retry to resolve the job. */
  version?: string | number;
  /** Display name, used for the retry toasts. */
  fileName?: string;
}

/**
 * Fetches (and, while non-terminal, polls) conversion-control's PipelineJob status for a file's
 * baseFileId and renders it as a colored Tag. Renders "-" for non-convertible formats or files
 * that have never been through the convert trigger (no PipelineJob exists yet -- not an error).
 */
export const ConversionStatusCell: React.FC<ConversionStatusCellProps> = ({
  baseFileId,
  format,
  version,
  fileName,
}) => {
  const { t } = useTranslation("files");
  const dispatch = useDispatchRoot();
  const entry = useSelectorRoot((state) =>
    selectPipelineStatusByBaseFileId(state, baseFileId)
  );
  const reconvert = useSelectorRoot((state) =>
    selectReconvertByBaseFileId(state, baseFileId)
  );
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const isConvertible = !!format && CONVERTIBLE_FORMATS.includes(format.toLowerCase());

  useEffect(() => {
    if (!isConvertible || !baseFileId) return;

    console.debug("[ConversionStatusCell] fetching status for", baseFileId);
    dispatch(pipelineStatusActions.fetchPipelineStatus({ baseFileId }));

    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
    // Only re-fetch when the underlying file identity changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseFileId, isConvertible]);

  useEffect(() => {
    const isNonTerminal =
      entry?.data != null && NON_TERMINAL_STATUSES.has(entry.data.overallStatus);

    if (!isConvertible || !baseFileId || !isNonTerminal) {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
      return;
    }

    if (!pollRef.current) {
      pollRef.current = setInterval(() => {
        console.debug("[ConversionStatusCell] polling status for", baseFileId);
        dispatch(pipelineStatusActions.fetchPipelineStatus({ baseFileId }));
      }, POLL_INTERVAL_MS);
    }

    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [baseFileId, isConvertible, entry?.data?.overallStatus, dispatch]);

  // Re-runs the existing PipelineJob for this file revision. Same action as the context menu's
  // "Reconvert" -- both go through pipelineStatus' reconvertEpic, which owns the retry/toast
  // behaviour and refreshes this cell when the job reopens.
  const handleRetry = (event: React.MouseEvent) => {
    // The row's own click/double-click handlers open the file -- a retry must not do that too.
    event.stopPropagation();
    if (!baseFileId) return;

    console.debug("[ConversionStatusCell] retry requested for", baseFileId);
    dispatch(
      pipelineStatusActions.reconvertRequested({
        baseFileId,
        baseMajorRev: Number(version) || 1,
        baseMinorRev: 0,
        fileName,
      })
    );
  };

  if (!isConvertible) {
    return <span>-</span>;
  }

  if (entry?.data != null) {
    // Falls back gracefully (instead of crashing) if the backend ever returns a status value
    // this frontend doesn't know about yet.
    const tag = STATUS_TAG[entry.data.overallStatus] ?? {
      color: "default",
      labelKey: "convert_status_queued",
    };
    const canRetry = RETRYABLE_STATUSES.has(entry.data.overallStatus);

    return (
      <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
        <Tag
          color={tag.color}
          style={{ borderRadius: 8, fontSize: 15, padding: "2px 10px", marginInlineEnd: 0 }}
        >
          {t(tag.labelKey)}
        </Tag>
        {canRetry && (
          <Tooltip title={t("convert_retry")}>
            <Button
              type="text"
              size="small"
              aria-label={t("convert_retry")}
              icon={<ReloadOutlined />}
              loading={reconvert?.loading}
              onClick={handleRetry}
            />
          </Tooltip>
        )}
      </span>
    );
  }

  if (entry?.loading) {
    return (
      <Tag style={{ borderRadius: 8, fontSize: 15, padding: "2px 10px" }}>
        {t("convert_status_running")}
      </Tag>
    );
  }

  // Fetched (or failed) with no job found -- not an error state worth surfacing to the user.
  return (
    <span title={entry?.error} style={{ opacity: 0.6 }}>
      {t("convert_status_not_converted")}
    </span>
  );
};

export default ConversionStatusCell;
